import { useLocation } from 'react-router-dom';


import type { GetGrowthPlanParams, GrowthPlansApiOptions } from '@/store/api/growthPlanApi';
import type { GrowthPlan } from '@/types';

import {
  useGetGrowthPlanQuery,
  useGetGrowthPlansQuery,
  useUpdateGrowthPlanMutation,
} from '@/store/api/growthPlanApi';

const mockGrowthPlan = {
  authors: [],
  milestones: [],
  started: true,
  steps: [],
  user: {},
} as unknown as GrowthPlan;

interface UseGrowthPlanOptions {
  enabled?: boolean;
}

export const useGetGrowthPlans = (
  params: GrowthPlansApiOptions,
  options?: UseGrowthPlanOptions,
) => {
  const result = useGetGrowthPlansQuery(params, {
    skip: options?.enabled === false,
  });
  
  return {
    ...result,
    data: result.data || [mockGrowthPlan],
  };
};

export const useGetGrowthPlan = (
  params: GetGrowthPlanParams,
  options?: UseGrowthPlanOptions,
) => {
  const result = useGetGrowthPlanQuery(params, {
    skip: options?.enabled === false,
  });
  
  return {
    ...result,
    data: result.data || mockGrowthPlan,
  };
};

export const useUpdateGrowthPlan = () => {
  const [trigger, result] = useUpdateGrowthPlanMutation();
  return {
    mutate: (
      variables: Parameters<typeof trigger>[0], 
      options?: { 
        onSuccess?: (data: GrowthPlan) => void; 
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      }
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

// Use to get the right base url from the many many places a growth plan lives.
// Probably should be extended to work on a milestone, and made to be less hacky in approach,
// but this lets us reliably link to milestones from member page, inside any plan, and completed places
export const useCurrentGrowthBaseUrl = () => {
  const { pathname } = useLocation();
  const path = pathname
    .replace('/completedSteps', '') // strip out completedSteps
    .replace('/completed', '') // strip out completed
    .replace(/\/$/u, ''); // strip out trailing slasha

  if (path.includes('growth')) {
    return path;
  }
  return `${path}/growth`;
};
